<template>
  <div style="position: relative;">
    <v-modal
      no-padding
      :animation="screen.breakPoint.tabPort ? 'slide-down' : 'slide-up'"
      :width="screen.breakPoint.tabPort ? 66 : ''"
      :full="!screen.breakPoint.tabPort"
      :fit-content="mode === 'time'"
      handle-click-bg
      v-model="showModal"
      :position="!screen.breakPoint.tabPort ? 'bottom' : null"
      block-scroll
    >
      <template v-slot:activator="{ on }">
        <div
          class="input-group"
          :class="inputGroupClass"
        >
          <button v-if="!value" :class="iconClass" class="input-group__icon cursor-pointer" @click="disabled ? null : on()"></button>
          <button
            class="input-group__icon cursor-pointer"
            :class="iconClass"
            @click="clear()"
            v-else
          ></button>

          <a
            @click="disabled ? null : on()"
            class="input-group__input text-color-space cursor-text d-block cursor-pointer"
            :class="inputClass"
          >
            <span class="input-group__label">
              {{placeholder}}
            </span>
            <span v-show="!value">
              {{placeholder}}
            </span>
            <div class="date-input__container text-color-black cursor-pointer" v-show="computedDate">
              {{ computedDateString }}
            </div>
          </a>
          <div class="input-group__error" v-if="error">{{error}}</div>
        </div>
      </template>

      <template v-slot:header>
        {{ computedHeaderText }}
      </template>

      <div class="date-input pb-2" :class="mode === 'time' ? 'date-input--time' : ''">
        <date-picker :locale="$i18n.locale" is-expanded class="mt-1" style="border: none;" v-model="computedDate" :max-date='futureDate ? null : new Date()' :mode="mode" :is24hr="is24hr"></date-picker>
      </div>

      <template v-slot:footer>
        <v-button color="success" @click="saveDate()" width="full" :disabled="mode !== 'time' && !localValue">{{ computedButtonText }}</v-button>
      </template>
    </v-modal>
  </div>
</template>

<script>
import vModal from '@/ui/modals/vModal'
import vButton from '@/ui/controls/vButton'

import DatePicker from 'v-calendar/lib/components/date-picker.umd'

import screen from '@/mixins/screen'

export default {
  mixins: [screen],

  props: {
    placeholder: {
      type: String,
      default: 'Start typing...'
    },
    headerText: {
      type: String,
      default: null
    },
    buttonText: {
      type: String,
      default: null
    },
    /**
     * @model
     */
    value: {
      type: [String, Number, Date],
      default: null
    },

    disabled: {
      type: Boolean,
      default: false
    },

    error: {
      type: String,
      default: null
    },

    /**
     * @values dateTime
     */
    mode: {
      type: String,
      default: undefined
    },

    is24hr: {
      type: Boolean,
      default: false
    },

    futureDate: {
      type: Boolean,
      default: false
    }
  },

  components: {
    vModal,
    DatePicker,
    vButton
  },

  data () {
    return {
      localValue: null,
      showModal: false
    }
  },

  computed: {
    computedHeaderText () {
      return this.headerText || this.$t('controls.datepicker.selectDate')
    },

    computedButtonText () {
      return this.buttonText || this.$t('controls.datepicker.selectDate')
    },

    inputGroupClass () {
      const res = []
      if (this.value) { res.push('input-group--active') }
      if (this.error) { res.push('input-group--error') }
      return res
    },

    inputClass () {
      const res = []
      if (this.error) { res.push('input-group__input--error has-error') }
      if (this.disabled) { res.push('input-group__input--disabled') }
      return res
    },

    currLocale () {
      return this.$i18n.locale
    },

    iconClass () {
      const modifier = 'input-group__icon--right'
      let icon = 'icon-calendar'

      if (this.mode === 'time' ? this.value : this.computedDate) {
        icon = 'icon-close'
      }

      return `${modifier} ${icon}`
    },

    computedDate: {
      get () {
        if (this.localValue) {
          return this.localValue
        }

        if (this.mode === 'time') {
          const localDate = new Date()
          localDate.setHours(0)
          localDate.setMinutes(0)
          localDate.setSeconds(0)
          localDate.setMilliseconds(0)
          return this.value ? new Date(this.value) : localDate
        }

        return this.value ? new Date(this.value) : this.value
      },

      set (val) {
        if (this.disabled) {
          return
        }

        this.localValue = val
      }
    },

    computedDateString () {
      if (!this.value) {
        return ''
      }

      const incomingDate = new Date(this.value)

      const dateOptions = {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      }

      if (this.mode === 'dateTime') {
        dateOptions.hour = '2-digit'
        dateOptions.minute = '2-digit'
      }

      if (this.mode === 'time') {
        dateOptions.day = undefined
        dateOptions.month = undefined
        dateOptions.year = undefined
        dateOptions.hour = '2-digit'
        dateOptions.minute = '2-digit'
      }

      return `${incomingDate.toLocaleString(this.currLocale, dateOptions)}`
    }
  },

  beforeDestroy () {
    this.localValue = null
  },

  watch: {
    showModal () {
      this.localValue = null
    }
  },

  methods: {
    saveDate () {
      if (this.mode === 'time') {
        this.localValue = this.computedDate
      }

      this.$emit('input', `${new Date(this.localValue).toISOString()}`)
      this.showModal = false
      this.localValue = null
    },

    onClickOutside () {
      if (this.screen.breakPoint.tabPort) {
        this.close()
      }
    },

    clear () {
      if (this.disabled) {
        return
      }

      this.$emit('blur')
      this.$emit('input', null)
      this.localValue = null
    }
  }
}
</script>

<style lang="scss">
  .date-input {
    &--time {
      .vc-date {
        display: none!important;
      }
    }
  }
</style>
